import { useEffect, useRef, useState } from "react";
import { Card } from "@threadws/jute";
import type { CalendarEvents } from "../../api/generated";
import { today, type Day } from "../../utils/calendarDay";
import { dayLanes } from "../../utils/dayLanes";
import { hourWindow, minutesInto } from "../../utils/dayLayout";
import { personaColors } from "../../utils/personaColors";
import { useNow } from "../../utils/useNow";
import { DayHeading } from "./DayHeading";
import { DayToolbar } from "./DayToolbar";
import { LaneHeader } from "./LaneHeader";
import { PersonaLane } from "./PersonaLane";
import { usePlannedBlocks } from "./usePlannedBlocks";

const HOUR_HEIGHT = 64;
const pad = (n: number) => String(n).padStart(2, "0");

interface DayCalendarProps {
  day: Day;
  range: { from: string; to: string };
  data: CalendarEvents;
  onDayChange: (day: Day) => void;
}

/**
 * One day as a column per persona on an hour grid, with a line at the current time. Time the
 * day planner set aside for tasks shows in the task's persona's column.
 */
export function DayCalendar({ day, range, data, onDayChange }: DayCalendarProps) {
  const now = useNow();
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const scroller = useRef<HTMLDivElement>(null);
  const blocks = usePlannedBlocks(day);

  const calendars = new Map(data.calendars.map((calendar) => [calendar.id, calendar]));
  const personaColor = personaColors(data.personas.map((persona) => persona.id));
  const colorOf = (id: string) =>
    calendars.get(id)?.color ?? personaColor.get(calendars.get(id)?.personaIds[0] ?? "") ?? "var(--accent)";
  const shown = data.events.filter((event) => !hidden.has(event.calendarId));
  const minutes = (iso: string) => minutesInto(range, iso);
  const timed = [...shown.filter((e) => !e.allDay), ...blocks];
  const hours = hourWindow(timed.map((item) => ({ start: minutes(item.startsAt), end: minutes(item.endsAt) })));
  const y = (m: number) => ((m - hours.first * 60) / 60) * HOUR_HEIGHT;
  const nowMinutes = day === today() ? minutes(now.toISOString()) : null;

  // Open on the current time today, or 08:00 on other days.
  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = Math.max(0, y((nowMinutes ?? 8 * 60 + 90) - 90));
  }, [day]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id: string) =>
    setHidden((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  const lanes = dayLanes(data, shown, blocks);
  const laneColumns = `repeat(${lanes.length}, minmax(140px, 1fr))`;
  const minWidth = 56 + 140 * lanes.length;

  return (
    <Card className="flex flex-col gap-4">
      <DayHeading day={day} onChange={onDayChange} />
      <DayToolbar day={day} events={shown.length} planned={blocks.length} />
      <div ref={scroller} className="-mx-6 -mb-6 h-[620px] overflow-auto border-t border-border-subtle">
        <div
          className="sticky top-0 z-10 grid border-b border-border-subtle bg-surface-card"
          style={{ gridTemplateColumns: `56px ${laneColumns}`, minWidth }}
        >
          <div />
          {lanes.map((lane) => (
            <LaneHeader key={lane.persona.id} {...lane} hidden={hidden} onToggle={toggle} colorOf={colorOf} />
          ))}
        </div>
        <div className="relative" style={{ height: y(hours.last * 60), minWidth }}>
          {Array.from({ length: hours.last - hours.first + 1 }, (_, i) => hours.first + i).map((hour) => (
            <div key={hour} className="absolute inset-x-0 border-t border-border-subtle" style={{ top: y(hour * 60) }}>
              <span className="absolute -top-2 left-0 w-[46px] bg-surface-card pr-0.5 text-right font-mono text-[10.5px] text-text-secondary">
                {hour < hours.last ? `${pad(hour % 24)}:00` : ""}
              </span>
            </div>
          ))}
          <div className="absolute inset-y-0 right-0 left-14 grid" style={{ gridTemplateColumns: laneColumns }}>
            {lanes.map((lane) => (
              <PersonaLane key={lane.persona.id} events={lane.timed} blocks={lane.blocks} range={range} firstHour={hours.first} hourHeight={HOUR_HEIGHT} colorOf={colorOf} blockColor={personaColor.get(lane.persona.id) ?? "var(--accent)"} now={now} />
            ))}
          </div>
          {nowMinutes !== null && nowMinutes >= hours.first * 60 && nowMinutes <= hours.last * 60 && (
            <div className="pointer-events-none absolute right-0 left-12 z-[5] border-t-[1.5px] border-accent" style={{ top: y(nowMinutes) }}>
              <span className="absolute -top-2.5 -left-[46px] rounded-sm bg-accent px-1 py-0.5 font-babyblue text-pixel-1 text-text-on-accent">
                {`${pad(now.getHours())}:${pad(now.getMinutes())}`}
              </span>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
